(() => {
  'use strict';

  const ROOT = () => window.Shopify?.routes?.root || '/';
  const PAIR_ID = '_oni_pair_id';
  const PAIR_ROLE = '_oni_pair_role';
  let timer = null;
  let reconciling = false;

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  let checkoutGuarding = false;
  let pendingCartRequests = 0;
  let aiPairing = false;
  let cartLayoutBusy = false;
  let lastCartLayoutSignature = '';

  const AI_CALL_PRODUCTS = {
    lite: {
      monthlyTitle: 'AI Call Assistant Lite Monthly',
      setupTitle: 'AI Call Assistant Lite Setup Fee',
      monthlyProductId: 10565435654410,
      setupProductId: 10565435457802,
      monthlyHandle: 'ai-call-assistant-lite-monthly',
      setupHandle: 'ai-call-assistant-lite-setup-fee',
      minutes: 100,
      setupPrice: '$99',
      monthlyPrice: '$49/month'
    },
    plus: {
      monthlyTitle: 'AI Call Assistant Plus Monthly',
      setupTitle: 'AI Call Assistant Plus Setup Fee',
      monthlyProductId: 10565435982090,
      setupProductId: 10565435916554,
      monthlyHandle: 'ai-call-assistant-plus-monthly',
      setupHandle: 'ai-call-assistant-plus-setup-fee',
      minutes: 250,
      setupPrice: '$149',
      monthlyPrice: '$89/month'
    },
    pro: {
      monthlyTitle: 'AI Call Assistant Pro Monthly',
      setupTitle: 'AI Call Assistant Pro Setup Fee',
      monthlyProductId: 10565436866826,
      setupProductId: 10565436145930,
      monthlyHandle: 'ai-call-assistant-pro-monthly',
      setupHandle: 'ai-call-assistant-pro-setup-fee',
      minutes: 500,
      setupPrice: '$249',
      monthlyPrice: '$149/month'
    }
  };

  const productCache = new Map();
  let productCatalogPromise = null;

  async function getCart() {
    const response = await fetch(`${ROOT()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }


  async function getProductCatalog() {
    if (!productCatalogPromise) {
      productCatalogPromise = fetch(`${ROOT()}products.json?limit=250`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(async (response) => {
        if (!response.ok) throw new Error('Unable to load product catalog');
        const payload = await response.json();
        return Array.isArray(payload?.products) ? payload.products : [];
      });
    }
    return productCatalogPromise;
  }

  async function getProduct(handle, productId) {
    const cacheKey = String(productId || handle);
    if (productCache.has(cacheKey)) return productCache.get(cacheKey);

    const request = (async () => {
      let resolvedHandle = handle;

      if (productId) {
        try {
          const catalog = await getProductCatalog();
          const match = catalog.find((product) => Number(product.id) === Number(productId));
          if (match?.handle) resolvedHandle = match.handle;
        } catch (_) {}
      }

      const response = await fetch(`${ROOT()}products/${encodeURIComponent(resolvedHandle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Unable to load ${resolvedHandle}`);
      return response.json();
    })();

    productCache.set(cacheKey, request);
    return request;
  }

  function firstVariant(product) {
    const variant = product?.variants?.[0];
    if (!variant) throw new Error('Product variant could not be found');
    return variant;
  }

  function sellingPlanId(product, variant) {
    return variant?.selling_plan_allocations?.[0]?.selling_plan_id ||
      product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id || null;
  }

  function itemHasPair(item) {
    const properties = item?.properties || {};
    return Boolean(properties[PAIR_ID] && properties[PAIR_ROLE]);
  }

  function titleMatches(item, title) {
    return String(item?.product_title || item?.title || '').trim().toLowerCase() === title.toLowerCase();
  }

  function productMatches(item, productId, title) {
    if (Number(item?.product_id) === Number(productId)) return true;
    return titleMatches(item, title);
  }

  function pairProperties(pairId, role, tier) {
    return {
      [PAIR_ID]: pairId,
      [PAIR_ROLE]: role,
      _oni_pair_type: 'ai_call_assistant',
      _oni_ai_call_tier: tier
    };
  }

  async function changeLine(item, quantity, properties) {
    const body = {
      id: item.key,
      quantity,
      properties
    };
    const planId = item?.selling_plan_allocation?.selling_plan?.id;
    if (planId) body.selling_plan = planId;

    const response = await fetch(`${ROOT()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('Unable to link cart item');
    return response.json();
  }

  async function addProductPairHalf(product, quantity, properties, requireSellingPlan) {
    const variant = firstVariant(product);
    const item = {
      id: variant.id,
      quantity,
      properties
    };
    if (requireSellingPlan) {
      const planId = sellingPlanId(product, variant);
      if (!planId) throw new Error(`No subscription selling plan found for ${product.title}`);
      item.selling_plan = planId;
    }

    const response = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item] })
    });
    if (!response.ok) throw new Error('Unable to add linked cart item');
    return response.json();
  }

  async function ensureAICallPairs() {
    if (aiPairing || reconciling) return getCart().catch(() => null);
    aiPairing = true;
    setCheckoutBusy(true);

    try {
      let cart = await getCart();

      for (const [tier, config] of Object.entries(AI_CALL_PRODUCTS)) {
        let monthlyItems = (cart.items || []).filter((item) => productMatches(item, config.monthlyProductId, config.monthlyTitle));
        let setupItems = (cart.items || []).filter((item) => productMatches(item, config.setupProductId, config.setupTitle));

        const unpairedMonthly = monthlyItems.filter((item) => !itemHasPair(item));
        const unpairedSetup = setupItems.filter((item) => !itemHasPair(item));
        const count = Math.max(unpairedMonthly.length, unpairedSetup.length);

        for (let index = 0; index < count; index += 1) {
          let monthly = unpairedMonthly[index] || null;
          let setup = unpairedSetup[index] || null;
          const pairId = `ai-call-${tier}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const quantity = Math.max(1, Number(monthly?.quantity || setup?.quantity || 1));

          /*
            Complete the pair while the checkout is locked and suppress recursive cart
            reconciliation for our own requests. Add a missing monthly subscription first,
            then tag the setup-fee line. This prevents the temporary one-line state from
            being interpreted as a broken pair and removed.
          */
          if (!monthly) {
            const product = await getProduct(config.monthlyHandle, config.monthlyProductId);
            await addProductPairHalf(product, quantity, pairProperties(pairId, 'subscription', tier), true);
            cart = await getCart();
            monthly = (cart.items || []).find((item) =>
              productMatches(item, config.monthlyProductId, config.monthlyTitle) &&
              String(item?.properties?.[PAIR_ID] || '') === pairId
            ) || null;
          } else {
            await changeLine(monthly, quantity, pairProperties(pairId, 'subscription', tier));
          }

          if (!setup) {
            const product = await getProduct(config.setupHandle, config.setupProductId);
            await addProductPairHalf(product, quantity, pairProperties(pairId, 'setup_fee', tier), false);
          } else {
            await changeLine(setup, quantity, pairProperties(pairId, 'setup_fee', tier));
          }

          cart = await getCart();
          monthlyItems = (cart.items || []).filter((item) => productMatches(item, config.monthlyProductId, config.monthlyTitle));
          setupItems = (cart.items || []).filter((item) => productMatches(item, config.setupProductId, config.setupTitle));
        }
      }

      return cart;
    } finally {
      aiPairing = false;
      if (!pendingCartRequests && !reconciling && !checkoutGuarding) setCheckoutBusy(false);
    }
  }

  async function synchronizeCart() {
    await ensureAICallPairs();
    return reconcile();
  }

  function groupedPairs(cart) {
    const groups = new Map();
    for (const item of cart.items || []) {
      const properties = item.properties || {};
      const id = properties[PAIR_ID];
      const role = properties[PAIR_ROLE];
      if (!id || !role) continue;
      if (!groups.has(id)) groups.set(id, { subscription: [], setup_fee: [] });
      if (role === 'subscription' || role === 'setup_fee') groups.get(id)[role].push(item);
    }
    return groups;
  }

  function cartPairsAreValid(cart) {
    for (const group of groupedPairs(cart).values()) {
      if (!group.subscription.length || !group.setup_fee.length) return false;
      const subscriptionQty = Number(group.subscription[0]?.quantity || 0);
      if (!subscriptionQty) return false;
      if (group.subscription.some((item) => Number(item.quantity) !== subscriptionQty)) return false;
      if (group.setup_fee.some((item) => Number(item.quantity) !== subscriptionQty)) return false;
    }
    return true;
  }

  function checkoutElements() {
    return [...document.querySelectorAll(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-checkout], [class*="checkout" i], [id*="checkout" i]'
    )].filter((el) => {
      const text = `${el.textContent || ''} ${el.value || ''} ${el.getAttribute?.('aria-label') || ''}`;
      const href = el.getAttribute?.('href') || '';
      return /checkout|check out|afrekenen/i.test(text) || /\/checkout(?:\?|$)/i.test(href);
    });
  }

  function setCheckoutBusy(isBusy) {
    document.documentElement.classList.toggle('oni-pair-cart-busy', Boolean(isBusy));
    checkoutElements().forEach((el) => {
      if (isBusy) {
        if (!el.hasAttribute('data-oni-original-disabled')) {
          el.setAttribute('data-oni-original-disabled', el.disabled ? 'true' : 'false');
        }
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('aria-disabled');
        el.removeAttribute('data-oni-original-disabled');
      }
    });
  }

  function beginCartRequest() {
    pendingCartRequests += 1;
    setCheckoutBusy(true);
  }

  function endCartRequest() {
    pendingCartRequests = Math.max(0, pendingCartRequests - 1);
    if (!pendingCartRequests && !reconciling && !checkoutGuarding) setCheckoutBusy(false);
  }

  async function reconcile() {
    if (reconciling) return getCart().catch(() => null);
    reconciling = true;
    setCheckoutBusy(true);
    try {
      const cart = await getCart();
      const updates = {};

      for (const group of groupedPairs(cart).values()) {
        const subscriptions = group.subscription;
        const setupFees = group.setup_fee;

        // The pair is inseparable. If either half is removed, remove the remaining half
        // before checkout can continue.
        if (!subscriptions.length || !setupFees.length) {
          [...subscriptions, ...setupFees].forEach((item) => { updates[item.key] = 0; });
          continue;
        }

        const desiredQuantity = Math.max(1, Number(subscriptions[0].quantity) || 1);
        subscriptions.forEach((item) => {
          if (Number(item.quantity) !== desiredQuantity) updates[item.key] = desiredQuantity;
        });
        setupFees.forEach((item) => {
          if (Number(item.quantity) !== desiredQuantity) updates[item.key] = desiredQuantity;
        });
      }

      if (!Object.keys(updates).length) return cart;

      const response = await fetch(`${ROOT()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Unable to synchronize linked cart items');
      const updatedCart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: updatedCart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: updatedCart } }));
      return updatedCart;
    } finally {
      reconciling = false;
      if (!pendingCartRequests && !checkoutGuarding) setCheckoutBusy(false);
    }
  }

  function schedule(delay = 180) {
    clearTimeout(timer);
    timer = setTimeout(() => synchronizeCart().catch(() => {}), delay);
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(String(url || ''));
  }

  function patchNetwork() {
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const url = String(args[0]?.url || args[0] || '');
      const isMutation = !reconciling && !aiPairing && isCartMutationUrl(url);
      if (isMutation) beginCartRequest();
      try {
        const response = await originalFetch.apply(this, args);
        if (isMutation) schedule(0);
        return response;
      } finally {
        if (isMutation) endCartRequest();
      }
    };

    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__oniPairCartUrl = String(url || '');
      return originalOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function(...args) {
      const isMutation = !reconciling && !aiPairing && isCartMutationUrl(this.__oniPairCartUrl || '');
      if (isMutation) {
        beginCartRequest();
        this.addEventListener('loadend', () => {
          schedule(0);
          endCartRequest();
        }, { once: true });
      }
      return originalSend.apply(this, args);
    };
  }

  function checkoutIntentTarget(event) {
    const target = event.target instanceof Element ? event.target.closest(
      'a, button, input[type="submit"], [role="button"]'
    ) : null;
    if (!target) return null;
    const text = `${target.textContent || ''} ${target.value || ''} ${target.getAttribute('aria-label') || ''}`;
    const href = target.getAttribute('href') || '';
    const name = target.getAttribute('name') || '';
    if (/\/checkout(?:\?|$)/i.test(href) || /checkout|check out|afrekenen/i.test(text) || name === 'checkout') return target;
    return null;
  }

  async function guardCheckout(event) {
    const target = checkoutIntentTarget(event);
    if (!target || checkoutGuarding) {
      if (checkoutGuarding && target) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }

    // Capture the click before AOV or Shopify can navigate. The cart is checked and
    // repaired server-side first, eliminating the brief timing window after removal.
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutGuarding = true;
    setCheckoutBusy(true);

    try {
      clearTimeout(timer);
      let cart = await synchronizeCart();
      if (!cart) cart = await getCart();

      // Re-read once after reconciliation so checkout only starts from a confirmed cart.
      cart = await getCart();
      if (!cartPairsAreValid(cart)) {
        cart = await synchronizeCart();
        cart = await getCart();
      }

      if (!cartPairsAreValid(cart)) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
        return;
      }

      if (!cart.items?.length) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
        return;
      }

      window.location.assign(`${ROOT()}checkout`);
    } catch (_) {
      // Fail closed: do not allow checkout until the linked cart can be verified.
      schedule(0);
    } finally {
      checkoutGuarding = false;
      if (!pendingCartRequests && !reconciling) setCheckoutBusy(false);
    }
  }

  function addGuardStyles() {
    const style = document.createElement('style');
    style.textContent = `
      html.oni-pair-cart-busy a[href*="/checkout"],
      html.oni-pair-cart-busy button[name="checkout"],
      html.oni-pair-cart-busy input[name="checkout"],
      html.oni-pair-cart-busy [data-checkout],
      html.oni-pair-cart-busy [class*="checkout" i] {
        pointer-events: none !important;
        opacity: .62 !important;
        cursor: wait !important;
      }
      .oni-ai-call-product-note {
        margin-top: 3px !important;
        font-size: 11px !important;
        line-height: 1.3 !important;
        color: rgba(20, 20, 20, .66) !important;
        max-width: 290px !important;
      }
      .oni-ai-call-product-note strong {
        color: #111 !important;
        font-weight: 700 !important;
      }
      .oni-ai-call-upsell-card .oni-ai-call-hidden-image {
        display: none !important;
      }
      .oni-ai-call-upsell-card img[data-oni-ai-call-hidden-image="true"] {
        display: none !important;
      }
      .oni-ai-call-title-row {
        display: inline-flex !important;
        align-items: center !important;
        gap: 6px !important;
        max-width: 100% !important;
      }
      .oni-ai-call-info-button {
        width: 18px !important;
        height: 18px !important;
        min-width: 18px !important;
        padding: 0 !important;
        border: 1px solid rgba(20,20,20,.3) !important;
        border-radius: 50% !important;
        background: transparent !important;
        color: #111 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        font: 700 11px/1 Arial, sans-serif !important;
        cursor: pointer !important;
        box-shadow: none !important;
      }
      .oni-ai-call-info-button:hover {
        background: rgba(20,20,20,.07) !important;
      }
      .oni-ai-call-info-modal {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483646 !important;
        display: none !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px !important;
        background: rgba(15,15,15,.46) !important;
      }
      .oni-ai-call-info-modal.is-open {
        display: flex !important;
      }
      .oni-ai-call-info-panel {
        width: min(420px, 100%) !important;
        max-height: min(680px, calc(100vh - 40px)) !important;
        overflow: auto !important;
        position: relative !important;
        border: 1px solid rgba(20,20,20,.12) !important;
        border-radius: 18px !important;
        background: #fffaf0 !important;
        color: #111 !important;
        padding: 22px !important;
        box-shadow: 0 24px 70px rgba(0,0,0,.24) !important;
        font-family: inherit !important;
      }
      .oni-ai-call-info-close {
        position: absolute !important;
        top: 12px !important;
        right: 12px !important;
        width: 30px !important;
        height: 30px !important;
        border: 0 !important;
        border-radius: 50% !important;
        background: rgba(20,20,20,.07) !important;
        color: #111 !important;
        font: 500 20px/1 Arial, sans-serif !important;
        cursor: pointer !important;
      }
      .oni-ai-call-info-kicker {
        margin: 0 40px 5px 0 !important;
        color: #8a744e !important;
        font-size: 10px !important;
        font-weight: 800 !important;
        letter-spacing: .12em !important;
        text-transform: uppercase !important;
      }
      .oni-ai-call-info-panel h3 {
        margin: 0 40px 8px 0 !important;
        font-size: 22px !important;
        line-height: 1.15 !important;
      }
      .oni-ai-call-info-price {
        margin: 0 0 16px !important;
        color: rgba(20,20,20,.68) !important;
        font-size: 13px !important;
      }
      .oni-ai-call-info-list {
        margin: 0 !important;
        padding: 0 !important;
        list-style: none !important;
        display: grid !important;
        gap: 10px !important;
      }
      .oni-ai-call-info-list li {
        position: relative !important;
        margin: 0 !important;
        padding-left: 24px !important;
        color: #282621 !important;
        font-size: 13px !important;
        line-height: 1.45 !important;
      }
      .oni-ai-call-info-list li::before {
        content: '✓' !important;
        position: absolute !important;
        left: 0 !important;
        top: 0 !important;
        width: 16px !important;
        height: 16px !important;
        border-radius: 50% !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        background: #b5f43c !important;
        color: #111 !important;
        font-size: 10px !important;
        font-weight: 900 !important;
      }
      .oni-ai-call-info-note {
        margin: 16px 0 0 !important;
        padding: 11px 12px !important;
        border-radius: 10px !important;
        background: rgba(20,20,20,.055) !important;
        color: rgba(20,20,20,.7) !important;
        font-size: 11px !important;
        line-height: 1.45 !important;
      }
      .oni-hide-native-cart-line {
        display: none !important;
      }
      .oni-compact-cart-overview {
        display: grid !important;
        gap: 12px !important;
        padding: 10px 14px 14px !important;
        background: transparent !important;
      }
      .oni-compact-overview-section {
        display: grid !important;
        gap: 0 !important;
        overflow: hidden !important;
        border: 1px solid rgba(17,17,17,.13) !important;
        border-radius: 12px !important;
        background: rgba(255,255,255,.48) !important;
      }
      .oni-compact-overview-heading {
        padding: 9px 12px !important;
        border-bottom: 1px solid rgba(17,17,17,.12) !important;
        background: #eeeae1 !important;
        color: #111 !important;
        font-size: 10px !important;
        font-weight: 850 !important;
        letter-spacing: .1em !important;
        line-height: 1.15 !important;
        text-transform: uppercase !important;
      }
      .oni-compact-overview-line {
        display: grid !important;
        grid-template-columns: minmax(0,1fr) auto !important;
        align-items: center !important;
        gap: 12px !important;
        min-height: 50px !important;
        padding: 8px 12px !important;
        border-top: 1px solid rgba(17,17,17,.09) !important;
      }
      .oni-compact-overview-section .oni-compact-overview-line:first-of-type {
        border-top: 0 !important;
      }
      .oni-compact-overview-copy {
        display: grid !important;
        gap: 2px !important;
        min-width: 0 !important;
      }
      .oni-compact-overview-copy strong {
        overflow: hidden !important;
        color: #111 !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1.22 !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }
      .oni-compact-overview-copy span {
        color: rgba(17,17,17,.58) !important;
        font-size: 10.5px !important;
        line-height: 1.2 !important;
      }
      .oni-compact-overview-actions {
        display: flex !important;
        align-items: center !important;
        gap: 10px !important;
      }
      .oni-compact-qty {
        display: inline-grid !important;
        grid-template-columns: 24px 24px 24px !important;
        height: 28px !important;
        overflow: hidden !important;
        border: 1px solid rgba(17,17,17,.12) !important;
        background: #fff !important;
      }
      .oni-compact-qty button,
      .oni-compact-qty span {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-width: 0 !important;
        min-height: 0 !important;
        width: 24px !important;
        height: 28px !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: transparent !important;
        color: #111 !important;
        font: 500 12px/1 Arial,sans-serif !important;
        box-shadow: none !important;
      }
      .oni-compact-qty button { cursor: pointer !important; }
      .oni-compact-overview-price {
        min-width: 62px !important;
        color: #111 !important;
        font-size: 13px !important;
        font-weight: 650 !important;
        text-align: right !important;
        white-space: nowrap !important;
      }
      .oni-compact-remove {
        width: 22px !important;
        height: 22px !important;
        min-width: 22px !important;
        min-height: 22px !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        background: transparent !important;
        color: rgba(17,17,17,.55) !important;
        font: 400 18px/1 Arial,sans-serif !important;
        cursor: pointer !important;
        box-shadow: none !important;
      }
      .oni-ai-call-upsell-card .oni-ai-call-hidden-image,
      .oni-ai-call-upsell-card img[data-oni-ai-call-hidden-image="true"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }


  function exactAddButtons(root) {
    return [...root.querySelectorAll('button, [role="button"]')].filter((button) =>
      /^add$/i.test(String(button.textContent || '').replace(/\s+/g, ' ').trim())
    );
  }

  function findUpsellCardFromAddButton(button, productTitles) {
    let node = button;
    for (let depth = 0; node && depth < 6; depth += 1, node = node.parentElement) {
      if (!(node instanceof HTMLElement)) continue;
      const text = String(node.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const containsProduct = productTitles.some((title) => text.includes(title));
      const alreadyMarked = Boolean(node.dataset.oniAiCallUpsellTier);
      const addCount = exactAddButtons(node).length;
      const hasCheckout = /checkout|continue to checkout/i.test(text);
      if ((containsProduct || alreadyMarked) && addCount === 1 && !hasCheckout) return node;
    }
    return null;
  }

  function hideUpsellImage(card) {
    if (!(card instanceof HTMLElement)) return;
    card.classList.add('oni-ai-call-upsell-card');

    const images = [...card.querySelectorAll('img')];
    images.forEach((img) => {
      img.dataset.oniAiCallHiddenImage = 'true';

      let wrapper = img.parentElement;
      for (let depth = 0; wrapper && wrapper !== card && depth < 3; depth += 1) {
        const text = String(wrapper.textContent || '').replace(/\s+/g, ' ').trim();
        const hasButton = Boolean(wrapper.querySelector('button, [role="button"]'));
        const imageCount = wrapper.querySelectorAll('img').length;

        if (!text && !hasButton && imageCount === 1) {
          wrapper.classList.add('oni-ai-call-hidden-image');
          break;
        }
        wrapper = wrapper.parentElement;
      }
    });
  }

  function ensureInfoModal() {
    let modal = document.getElementById('oni-ai-call-info-modal');
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = 'oni-ai-call-info-modal';
    modal.className = 'oni-ai-call-info-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="oni-ai-call-info-panel" role="dialog" aria-modal="true" aria-labelledby="oni-ai-call-info-title">
        <button type="button" class="oni-ai-call-info-close" aria-label="Close information">×</button>
        <p class="oni-ai-call-info-kicker">24/7 AI phone support</p>
        <h3 id="oni-ai-call-info-title"></h3>
        <p class="oni-ai-call-info-price"></p>
        <ul class="oni-ai-call-info-list">
          <li>Answers incoming customer calls using your business information.</li>
          <li>Explains opening hours, services, prices, location and common policies.</li>
          <li>Collects the caller’s name, phone number, reason for calling and message.</li>
          <li>Transfers important or complex calls to the number you choose.</li>
          <li>Directs callers to your existing booking process and can share a booking link when configured.</li>
          <li>Creates call summaries so you can quickly see what each caller needed.</li>
          <li>Can be updated when your services, prices or opening hours change.</li>
        </ul>
        <p class="oni-ai-call-info-note">Included minutes reset monthly. Direct appointment booking into a live calendar is not included unless separately configured.</p>
      </div>`;

    document.body.appendChild(modal);

    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.style.removeProperty('overflow');
    };

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('.oni-ai-call-info-close')) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    return modal;
  }

  function openInfoModal(tier, details) {
    const modal = ensureInfoModal();
    modal.querySelector('#oni-ai-call-info-title').textContent = details.displayTitle;
    modal.querySelector('.oni-ai-call-info-price').textContent = `${details.description}.`;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  function addInfoButton(element, card, tier, details) {
    if (!(element instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
    if (card.querySelector(`.oni-ai-call-info-button[data-tier="${tier}"]`)) return;

    let row = element.parentElement;
    if (!row || !row.classList.contains('oni-ai-call-title-row')) {
      row = document.createElement('span');
      row.className = 'oni-ai-call-title-row';
      element.parentNode.insertBefore(row, element);
      row.appendChild(element);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'oni-ai-call-info-button';
    button.dataset.tier = tier;
    button.setAttribute('aria-label', `What can ${details.displayTitle} do?`);
    button.textContent = 'i';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openInfoModal(tier, details);
    });
    row.appendChild(button);
  }

  function restoreNonUpsellDecorations(drawer, validCards) {
    drawer.querySelectorAll('.oni-ai-call-product-note').forEach((note) => {
      if (![...validCards].some((card) => card.contains(note))) note.remove();
    });

    drawer.querySelectorAll('.oni-ai-call-info-button').forEach((button) => {
      if (![...validCards].some((card) => card.contains(button))) button.remove();
    });

    drawer.querySelectorAll('[data-oni-ai-original-title]').forEach((element) => {
      if ([...validCards].some((card) => card.contains(element))) return;
      element.textContent = element.dataset.oniAiOriginalTitle || element.textContent;
      delete element.dataset.oniAiOriginalTitle;
      const row = element.parentElement;
      if (row?.classList.contains('oni-ai-call-title-row')) {
        row.parentNode.insertBefore(element, row);
        row.remove();
      }
    });
  }


  function relabelSubscriptionFrequency(drawer) {
    if (!(drawer instanceof HTMLElement)) return;

    const walker = document.createTreeWalker(
      drawer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const parent = node.parentElement;
          if (!parent || parent.closest('script, style, textarea, input, select')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    const matches = [];
    while (walker.nextNode()) {
      const value = String(walker.currentNode.nodeValue || '').replace(/\s+/g, ' ').trim();
      if (/^deliver every month$/i.test(value)) matches.push(walker.currentNode);
    }

    matches.forEach((node) => {
      node.nodeValue = String(node.nodeValue || '').replace(/deliver every month/i, 'Billed Monthly');
    });
  }


  function corePlanTitle(title) {
    return /^(starter|business|premium)\b/i.test(String(title || '').trim());
  }

  function isSetupFeeTitle(title) {
    return /setup fee/i.test(String(title || '').trim());
  }

  function recurringCartItem(item) {
    const title = String(item?.product_title || item?.title || '');
    return Boolean(item?.selling_plan_allocation) || /\b(monthly|yearly|billed monthly|billed yearly|deliver every month|deliver every year)\b/i.test(title);
  }

  function buildCartSectionHeading(label) {
    const heading = document.createElement('div');
    heading.className = 'oni-cart-section-heading';
    heading.textContent = label;
    return heading;
  }

  function detectAOVCartItemBlocks(body, cart) {
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    if (!cartItems.length) return [];

    const titleEntries = cartItems.map((item, index) => ({
      item,
      index,
      title: String(item?.product_title || item?.title || '').trim(),
      normalized: normalizeText(item?.product_title || item?.title || '')
    })).filter((entry) => entry.normalized);

    const titleEntriesSorted = [...titleEntries].sort((a, b) => b.normalized.length - a.normalized.length);

    return [...body.children].map((child, domIndex) => {
      if (!(child instanceof HTMLElement)) return null;
      if (
        child.id === 'oni-aov-cost-summary' ||
        child.classList.contains('oni-cart-section-heading') ||
        child.classList.contains('oni-compact-cart-overview')
      ) return null;
      if (exactAddButtons(child).length) return null;

      const text = normalizeText(child.textContent || '');
      if (!text) return null;
      if (/checkout|continue shopping|due today|then monthly|renews yearly|subtotal/i.test(text)) return null;

      const match = titleEntriesSorted.find((entry) => text.includes(entry.normalized));
      if (!match) return null;

      return {
        node: child,
        domIndex,
        item: match.item,
        title: match.title,
        normalizedTitle: match.normalized,
        cartIndex: match.index
      };
    }).filter(Boolean);
  }

  function formatCartMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `€${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  function cartGroupForItem(item) {
    const title = String(item?.product_title || item?.title || '').trim();
    const isCore = corePlanTitle(title);
    const isSetup = isSetupFeeTitle(title);
    const isRecurring = recurringCartItem(item);

    if (isCore) return { group: 'main', sort: isSetup ? 0 : (isRecurring ? 1 : 2) };
    if (isRecurring) return { group: 'monthly', sort: 0 };
    return { group: 'setup', sort: isSetup ? 0 : 1 };
  }

  function shortCartTitle(item) {
    return String(item?.product_title || item?.title || '')
      .replace(/\s+Monthly Payment$/i, '')
      .replace(/\s+Monthly$/i, '')
      .replace(/\s+Setup Fee$/i, ' setup fee')
      .replace(/\s+fee$/i, ' fee')
      .trim();
  }

  function buildCompactLine(item, currency) {
    const line = document.createElement('div');
    line.className = 'oni-compact-overview-line';
    line.dataset.cartKey = item.key;

    const recurring = recurringCartItem(item);
    const subtitle = recurring ? 'Billed monthly' : (isSetupFeeTitle(item.product_title || item.title) ? 'One-time setup fee' : 'One-time add-on');
    const linePrice = item.final_line_price ?? item.original_line_price ?? item.line_price ?? 0;

    line.innerHTML = `
      <div class="oni-compact-overview-copy">
        <strong>${shortCartTitle(item)}</strong>
        <span>${subtitle}</span>
      </div>
      <div class="oni-compact-overview-actions">
        <div class="oni-compact-qty" aria-label="Quantity controls">
          <button type="button" data-oni-qty="minus" aria-label="Decrease quantity">−</button>
          <span>${Number(item.quantity) || 1}</span>
          <button type="button" data-oni-qty="plus" aria-label="Increase quantity">+</button>
        </div>
        <strong class="oni-compact-overview-price">${formatCartMoney(linePrice, currency)}</strong>
        <button type="button" class="oni-compact-remove" data-oni-remove aria-label="Remove ${shortCartTitle(item)}">×</button>
      </div>`;

    return line;
  }

  async function updateCartLine(key, quantity) {
    setCheckoutBusy(true);
    try {
      const response = await fetch(`${ROOT()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart');
      const cart = await response.json();
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      return cart;
    } finally {
      setTimeout(() => setCheckoutBusy(false), 150);
    }
  }

  async function reorderAOVCartItems() {
    if (cartLayoutBusy) return;

    const drawer = document.querySelector('#aov-cart-drawer, [id*="aov-cart-drawer" i], [class*="AOV-CartDrawer"]');
    if (!drawer) return;
    const body = drawer.querySelector('.AOV-CartDrawer-Body');
    if (!body) return;

    let cart;
    try {
      cart = await getCart();
    } catch (_) {
      return;
    }

    const signature = JSON.stringify((cart.items || []).map((item) => [item.key, item.quantity, item.final_line_price]));
    let overview = body.querySelector(':scope > .oni-compact-cart-overview');

    /* AOV.ai can rebuild its native item rows without changing the Shopify cart.
       Always re-detect and hide those rows before deciding the custom overview is current. */
    const blocks = detectAOVCartItemBlocks(body, cart);
    blocks.forEach((block) => block.node.classList.add('oni-hide-native-cart-line'));

    if (overview && overview.dataset.signature === signature) {
      const firstNative = blocks[0]?.node;
      if (firstNative && overview.nextElementSibling !== firstNative) {
        body.insertBefore(overview, firstNative);
      }
      return;
    }

    cartLayoutBusy = true;
    try {
      if (!overview) {
        overview = document.createElement('div');
        overview.className = 'oni-compact-cart-overview';
        const firstNative = blocks[0]?.node || body.firstElementChild;
        body.insertBefore(overview, firstNative || null);
      }

      const groups = { main: [], setup: [], monthly: [] };
      (cart.items || []).forEach((item, index) => {
        const meta = cartGroupForItem(item);
        groups[meta.group].push({ item, sort: meta.sort, index });
      });
      Object.values(groups).forEach((group) => group.sort((a, b) => (a.sort - b.sort) || (a.index - b.index)));

      overview.innerHTML = '';
      const specs = [
        ['main', 'Main plan'],
        ['setup', 'Add-ons · setup fees'],
        ['monthly', 'Add-ons · billed monthly']
      ];

      specs.forEach(([key, label]) => {
        if (!groups[key].length) return;
        const section = document.createElement('section');
        section.className = 'oni-compact-overview-section';
        const heading = document.createElement('div');
        heading.className = 'oni-compact-overview-heading';
        heading.textContent = label;
        section.appendChild(heading);
        groups[key].forEach(({ item }) => section.appendChild(buildCompactLine(item, cart.currency)));
        overview.appendChild(section);
      });

      overview.dataset.signature = signature;

      overview.querySelectorAll('[data-oni-qty], [data-oni-remove]').forEach((button) => {
        button.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          const line = button.closest('.oni-compact-overview-line');
          if (!line) return;
          const item = (cart.items || []).find((entry) => entry.key === line.dataset.cartKey);
          if (!item) return;
          const current = Number(item.quantity) || 1;
          const quantity = button.hasAttribute('data-oni-remove') ? 0 : (button.dataset.oniQty === 'plus' ? current + 1 : Math.max(0, current - 1));
          button.disabled = true;
          try {
            await updateCartLine(item.key, quantity);
          } catch (_) {
            button.disabled = false;
          }
        });
      });
    } finally {
      setTimeout(() => { cartLayoutBusy = false; }, 120);
    }
  }

  function injectAOVDescriptions() {
    const drawer = document.querySelector('#aov-cart-drawer, [id*="aov-cart-drawer" i], [class*="AOV-CartDrawer"]');
    if (!drawer) return;

    relabelSubscriptionFrequency(drawer);

    const products = new Map();
    Object.entries(AI_CALL_PRODUCTS).forEach(([tier, config]) => {
      const details = {
        tier,
        displayTitle: `AI Call Assistant ${tier.charAt(0).toUpperCase()}${tier.slice(1)}`,
        description: `${config.setupPrice} setup + ${config.monthlyPrice} · ${config.minutes} minutes included monthly`
      };
      products.set(config.setupTitle.toLowerCase(), details);
      products.set(config.monthlyTitle.toLowerCase(), details);
    });

    const productTitles = [...products.keys()];
    const validCards = new Set();

    exactAddButtons(drawer).forEach((addButton) => {
      const card = findUpsellCardFromAddButton(addButton, productTitles);
      if (!card || validCards.has(card)) return;
      validCards.add(card);

      let titleElement = null;
      let details = null;

      card.querySelectorAll('*').forEach((element) => {
        if (titleElement || !(element instanceof HTMLElement)) return;
        if (element.children.length > 2) return;
        const text = String(element.dataset.oniAiOriginalTitle || element.textContent || '')
          .replace(/\s+/g, ' ').trim();
        const match = products.get(text.toLowerCase());
        if (match) {
          titleElement = element;
          details = match;
        }
      });

      if (!titleElement || !details) return;

      card.dataset.oniAiCallUpsellTier = details.tier;
      hideUpsellImage(card);
      const currentText = String(titleElement.textContent || '').replace(/\s+/g, ' ').trim();
      if (!titleElement.dataset.oniAiOriginalTitle) titleElement.dataset.oniAiOriginalTitle = currentText;
      if (currentText !== details.displayTitle) titleElement.textContent = details.displayTitle;
      addInfoButton(titleElement, card, details.tier, details);

      const existing = card.querySelector('.oni-ai-call-product-note');
      if (existing) {
        if (existing.textContent !== details.description) existing.textContent = details.description;
      } else {
        const note = document.createElement('div');
        note.className = 'oni-ai-call-product-note';
        note.textContent = details.description;
        titleElement.insertAdjacentElement('afterend', note);
      }
    });

    // Do not remove/rebuild decorations during AOV's brief internal rerenders.
    // AOV frequently replaces card contents in stages; cleanup here caused visible flicker.
  }

  function observeAOVDrawer() {
    let drawerObserver = null;
    let bodyObserver = null;
    let descriptionTimer = null;

    const scheduleDescriptionRefresh = () => {
      if (cartLayoutBusy) return;
      clearTimeout(descriptionTimer);
      descriptionTimer = setTimeout(() => {
        injectAOVDescriptions();
        reorderAOVCartItems().catch(() => {});
      }, 220);
    };

    const attachToDrawer = () => {
      const drawer = document.querySelector('#aov-cart-drawer, [id*="aov-cart-drawer" i], [class*="AOV-CartDrawer"]');
      if (!drawer) return false;

      if (bodyObserver) {
        bodyObserver.disconnect();
        bodyObserver = null;
      }

      if (drawerObserver) drawerObserver.disconnect();
      drawerObserver = new MutationObserver(scheduleDescriptionRefresh);
      drawerObserver.observe(drawer, { childList: true, subtree: true });

      scheduleDescriptionRefresh();
      return true;
    };

    if (attachToDrawer()) return;

    bodyObserver = new MutationObserver(() => {
      attachToDrawer();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    addGuardStyles();
    patchNetwork();
    observeAOVDrawer();
    document.addEventListener('click', guardCheckout, true);
    document.addEventListener('cart:updated', () => schedule(0));
    document.addEventListener('cart:refresh', () => schedule(0));
    schedule(0);
  }

  const start = () => {
    if (document.body?.dataset?.oniHome === 'true') {
      if ('requestIdleCallback' in window) requestIdleCallback(init, { timeout: 1000 });
      else setTimeout(init, 550);
    } else {
      init();
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
